import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { Email, fetchGmailEmails } from "@/app/helpers/google/gmail/fetchEmails"
import { useSession } from "next-auth/react"

interface GlobalContextProps {
  emails: Email[]
  setEmails: Dispatch<SetStateAction<Email[]>>
  refreshEmailDataInContext: () => Promise<void>
  loadingEmails: boolean
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const EmailContext: React.FC<GlobalProviderProps> = ({ children }) => {
  const [emails, setEmails] = useState<Email[]>([])
  const [loadingEmails, setLoadingEmails] = useState(false)
  const { data: session, status } = useSession() // Add this

  const refreshEmailDataInContext = useCallback(async () => {
    if (!session?.user) {
      console.log("EmailContext: No session, skipping email fetch")
      return
    }

    setLoadingEmails(true)

    try {
      const emailData = await fetchGmailEmails()
      if (emailData) {
        await setEmails(emailData)
      }
    } catch (error) {
      console.error("Failed to fetch emails:", error)
    } finally {
      setLoadingEmails(false)
    }
  }, [session?.user])

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return // Still loading
    if (status === "unauthenticated") return // Not logged in

    if (session?.user) {
      // Defer email fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      const deferFetch = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              refreshEmailDataInContext()
            },
            { timeout: 2000 }
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            refreshEmailDataInContext()
          }, 100) // Small delay to allow initial render
        }
      }
      deferFetch()
    }
  }, [session, status, refreshEmailDataInContext])

  return (
    <GlobalContext.Provider
      value={{ emails, setEmails, refreshEmailDataInContext, loadingEmails }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useEmailContext = () => useContext(GlobalContext)
